import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

interface UserCredits {
  id: string;
  user_id: string;
  balance: number;
  created_at: string;
  updated_at: string;
}

interface CreditTransaction {
  id: string;
  user_id: string;
  amount: number;
  type: "purchase" | "usage" | "bonus" | "refund";
  description: string;
  metadata: Json;
  created_at: string;
}

export function useCredits() {
  const queryClient = useQueryClient();

  const { data: credits, isLoading: creditsLoading } = useQuery({
    queryKey: ["user-credits"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("user_credits")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data as UserCredits | null;
    },
  });

  const { data: transactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ["credit-transactions"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("credit_transactions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as CreditTransaction[];
    },
  });

  const deductCredits = useMutation({
    mutationFn: async ({ amount, description, metadata }: { amount: number; description: string; metadata?: Record<string, unknown> }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase.rpc("deduct_credits", {
        p_user_id: user.id,
        p_amount: amount,
        p_description: description,
        p_metadata: (metadata || {}) as Json,
      });

      if (error) throw error;
      if (!data) throw new Error("Crédits insuffisants");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-credits"] });
      queryClient.invalidateQueries({ queryKey: ["credit-transactions"] });
    },
  });

  const addCredits = useMutation({
    mutationFn: async ({ amount, type, description, metadata }: { amount: number; type: string; description: string; metadata?: Record<string, unknown> }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase.rpc("add_credits", {
        p_user_id: user.id,
        p_amount: amount,
        p_type: type,
        p_description: description,
        p_metadata: (metadata || {}) as Json,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-credits"] });
      queryClient.invalidateQueries({ queryKey: ["credit-transactions"] });
    },
  });

  const hasEnoughCredits = (amount: number) => {
    return (credits?.balance ?? 0) >= amount;
  };

  return {
    balance: credits?.balance ?? 0,
    credits,
    transactions: transactions ?? [],
    creditsLoading,
    transactionsLoading,
    deductCredits,
    addCredits,
    hasEnoughCredits,
  };
}
